// src/main/shell/tree-search/Autocomplete.ts
// Discord-style dropdown autocomplete for search commands, with dynamic value
// suggestions from real item data. Ported from the symbol grid's
// SearchAutocomplete; generalized over the item type `T` and host context `C`.
//
// The two symbol-specific couplings are replaced by host-supplied closures:
//   - the hardcoded SEARCH_COMMANDS registry      → opts.commands
//   - the `toFlatSymbol` + `switch(command.name)` → opts.iterate() + opts.ctx()
//     value extraction                              + opts.valueForCommand()
// so this file has no dependency on any item-specific type.
import { getMatchingCommands, getCommand, parseSearchInput, matchesFilters, } from './engine';
import { t } from '../i18n';
/**
 * A command's description, by key.
 *
 * The keys come from the CALLER's command spec (the log tree's, the symbol tree's), so they are
 * plain strings and cannot be checked against the generated key union the way a literal can. The
 * cast is the honest expression of that: the checking happens where the spec is authored, not here.
 * It compiled before this file moved only because the import crossed a package boundary and picked
 * up the looser published types.
 */
const describe = (key) => t(key);
export class TreeAutocomplete {
    constructor(container, options) {
        this.dropdown = null;
        this.ghostOverlay = null;
        this.selectedIndex = 0;
        this.suggestions = [];
        this.visible = false;
        this.container = container;
        this.options = options;
        if (options.inlineGhost !== false)
            this.createGhostOverlay();
    }
    createGhostOverlay() {
        this.ghostOverlay = document.createElement('div');
        this.ghostOverlay.className = 'search-ghost-overlay';
        this.container.appendChild(this.ghostOverlay);
    }
    /**
     * Update autocomplete based on current input value and cursor position
     */
    update(inputValue, cursorPos, anchorRect) {
        const suggestions = this.generateSuggestions(inputValue, cursorPos);
        if (suggestions.length === 0) {
            this.hide();
            return;
        }
        this.suggestions = suggestions;
        this.selectedIndex = 0;
        this.visible = true;
        if (!this.dropdown) {
            this.dropdown = document.createElement('div');
            this.dropdown.className = 'search-autocomplete-dropdown';
            this.dropdownHost().appendChild(this.dropdown);
        }
        this.render();
        this.updateGhostText(inputValue);
        this.position(anchorRect);
        this.dropdown.style.display = 'flex';
    }
    /**
     * Where the dropdown must live. body by default — but when the anchor sits
     * inside an open NATIVE <dialog> (showModal → the browser TOP LAYER), a
     * body-mounted element paints UNDER the modal no matter its z-index; the only
     * way to paint above is to be a flat-tree descendant of the dialog. Mounting
     * next to the anchor (not inside the dialog's shadow root) keeps the global
     * .search-autocomplete-dropdown styles applying. position:fixed still means
     * viewport coordinates there — an open vitesse-dialog is transform:none at
     * rest, so it creates no containing block.
     */
    dropdownHost() {
        let n = this.container;
        while (n) {
            if (n instanceof HTMLDialogElement && n.open)
                return this.container;
            if (n instanceof Element && n.assignedSlot) {
                n = n.assignedSlot;
                continue;
            }
            if (n instanceof ShadowRoot) {
                n = n.host;
                continue;
            }
            n = n.parentNode;
        }
        return document.body;
    }
    generateSuggestions(inputValue, cursorPos) {
        // Find what token the cursor is in
        const beforeCursor = inputValue.slice(0, cursorPos);
        const lastSlashIndex = beforeCursor.lastIndexOf('/');
        // Not in a command context
        if (lastSlashIndex === -1) {
            return [];
        }
        const commandPart = beforeCursor.slice(lastSlashIndex + 1);
        const eqIndex = commandPart.indexOf('=');
        if (eqIndex === -1) {
            // User is typing command name (e.g., "/lab")
            return this.generateCommandSuggestions(commandPart, inputValue);
        }
        else {
            // User is typing value (e.g., "/label=ice")
            const cmdName = commandPart.slice(0, eqIndex);
            const valuePartial = commandPart.slice(eqIndex + 1);
            return this.generateValueSuggestions(cmdName, valuePartial, inputValue);
        }
    }
    generateCommandSuggestions(partial, fullInput) {
        const matchingCommands = partial === '' ? this.options.commands : getMatchingCommands(partial, this.options.commands);
        return matchingCommands.map((cmd) => {
            // Get a real example value if possible
            const exampleValue = this.getExampleValue(cmd, fullInput);
            return {
                display: `/${cmd.name}`,
                completion: `/${cmd.name}=${exampleValue} `,
                description: describe(cmd.descKey),
                type: 'command',
            };
        });
    }
    generateValueSuggestions(cmdName, valuePartial, fullInput) {
        const command = getCommand(cmdName, this.options.commands);
        if (!command)
            return [];
        const items = this.options.iterate();
        if (!items) {
            // No catalog to derive from — a consumer over a SERVER-PAGED view (the model
            // binding picker) has only a window, never the whole set. An ENUM command's
            // values are DECLARED, not derived, so they can still be offered in full;
            // deriving them from the loaded window would suggest "the types I happen to
            // hold", which is the same lie as filtering client-side.
            // A free-text command (/name, /label) genuinely has nothing to suggest.
            if (!command.enumValues?.length)
                return [];
            const partial = valuePartial.toLowerCase();
            return command.enumValues
                .filter((v) => !partial || v.toLowerCase().startsWith(partial))
                .map((v) => ({
                display: v,
                completion: `/${command.name}=${v} `,
                description: describe(command.descKey),
                type: 'value',
            }));
        }
        const ctx = this.options.ctx();
        // Get existing filters from the input (excluding the current one being typed)
        const existingFilters = this.getExistingFilters(fullInput, cmdName);
        // Collect possible values based on command type
        const possibleValues = new Set();
        const valueLower = valuePartial.toLowerCase();
        for (const item of items) {
            // Check if item matches existing filters
            if (existingFilters && !matchesFilters(item, existingFilters, ctx, this.options.textMatch)) {
                continue;
            }
            // Get the value for this command type
            const val = this.options.valueForCommand(command, item);
            if (val && val.toLowerCase().includes(valueLower)) {
                possibleValues.add(val);
            }
        }
        // Sort and limit results
        const sortedValues = Array.from(possibleValues)
            .sort((a, b) => {
            // Prioritize values that start with the partial
            const aStarts = a.toLowerCase().startsWith(valueLower);
            const bStarts = b.toLowerCase().startsWith(valueLower);
            if (aStarts && !bStarts)
                return -1;
            if (!aStarts && bStarts)
                return 1;
            return a.localeCompare(b);
        })
            .slice(0, 10); // Limit to 10 suggestions
        return sortedValues.map((val) => ({
            display: val,
            completion: `/${cmdName}=${val} `,
            description: describe(command.descKey),
            type: 'value',
        }));
    }
    getExampleValue(cmd, fullInput) {
        const items = this.options.iterate();
        if (!items) {
            // Fallback to placeholder
            return cmd.placeholder.replace('=', '');
        }
        const ctx = this.options.ctx();
        // Get existing filters
        const existingFilters = this.getExistingFilters(fullInput, cmd.name);
        // Find first matching item and get its value for this command
        for (const item of items) {
            // Check existing filters
            if (existingFilters && !matchesFilters(item, existingFilters, ctx, this.options.textMatch)) {
                continue;
            }
            // Get value for this command
            const val = this.options.valueForCommand(cmd, item);
            if (val)
                return val;
        }
        // Fallback
        return cmd.placeholder.replace('=', '');
    }
    getExistingFilters(fullInput, excludeCommand) {
        const parsed = parseSearchInput(fullInput, this.options.commands);
        if (parsed.commands.length === 0 && !parsed.textQuery) {
            return null;
        }
        // Filter out the command we're currently typing
        if (excludeCommand) {
            parsed.commands = parsed.commands.filter((c) => c.command.name !== excludeCommand);
        }
        return parsed;
    }
    hide() {
        if (this.dropdown) {
            this.dropdown.style.display = 'none';
        }
        if (this.ghostOverlay) {
            this.ghostOverlay.style.display = 'none';
        }
        this.visible = false;
        this.options.onClose();
    }
    isOpen() {
        return this.visible;
    }
    handleKeyDown(e) {
        if (!this.visible) {
            return false;
        }
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                e.stopPropagation();
                this.selectedIndex = Math.min(this.selectedIndex + 1, this.suggestions.length - 1);
                this.render();
                this.updateGhostTextFromSelection();
                return true;
            case 'ArrowUp':
                e.preventDefault();
                e.stopPropagation();
                this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
                this.render();
                this.updateGhostTextFromSelection();
                return true;
            case 'Enter':
            case 'Tab': {
                e.preventDefault();
                e.stopPropagation();
                const selected = this.suggestions[this.selectedIndex];
                if (selected) {
                    this.options.onSelect(selected.completion);
                    this.hide();
                }
                return true;
            }
            case 'Escape':
                e.preventDefault();
                e.stopPropagation();
                this.hide();
                return true;
            default:
                return false;
        }
    }
    updateGhostText(inputValue) {
        if (!this.ghostOverlay || !this.visible || this.suggestions.length === 0) {
            if (this.ghostOverlay)
                this.ghostOverlay.style.display = 'none';
            return;
        }
        const selected = this.suggestions[this.selectedIndex];
        if (!selected) {
            this.ghostOverlay.style.display = 'none';
            return;
        }
        // Find where the current command starts
        const lastSlashIndex = inputValue.lastIndexOf('/');
        if (lastSlashIndex === -1) {
            this.ghostOverlay.style.display = 'none';
            return;
        }
        const currentTyped = inputValue.slice(lastSlashIndex);
        // The completion without the leading /
        const completionWithoutSlash = selected.completion.slice(1);
        // Find how much of the completion the user has already typed
        const typedWithoutSlash = currentTyped.slice(1);
        // The grey part is what they haven't typed yet
        let greyPart = '';
        if (completionWithoutSlash.toLowerCase().startsWith(typedWithoutSlash.toLowerCase())) {
            greyPart = completionWithoutSlash.slice(typedWithoutSlash.length);
        }
        if (greyPart) {
            const invisiblePart = inputValue;
            this.ghostOverlay.innerHTML = `<span class="ghost-invisible">${this.escapeHtml(invisiblePart)}</span><span class="ghost-completion">${this.escapeHtml(greyPart)}</span>`;
            this.ghostOverlay.style.display = 'flex';
        }
        else {
            this.ghostOverlay.style.display = 'none';
        }
    }
    updateGhostTextFromSelection() {
        const searchInput = this.container.querySelector('input[type="text"]');
        if (searchInput) {
            this.updateGhostText(searchInput.value);
        }
    }
    escapeHtml(text) {
        return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
    render() {
        if (!this.dropdown)
            return;
        const html = `
      <div class="search-autocomplete-options">
        ${this.suggestions
            .map((sug, i) => `
          <div class="search-autocomplete-option ${i === this.selectedIndex ? 'selected' : ''}" data-index="${i}">
            <div class="search-autocomplete-option-content">
              <span class="search-autocomplete-cmd">${this.escapeHtml(sug.display)}</span>
              <span class="search-autocomplete-desc">${this.escapeHtml(sug.description)}</span>
            </div>
          </div>
        `)
            .join('')}
      </div>
    `;
        this.dropdown.innerHTML = html;
        const optionElements = this.dropdown.querySelectorAll('.search-autocomplete-option');
        optionElements.forEach((el, i) => {
            el.addEventListener('mousedown', (e) => {
                e.preventDefault();
                const sug = this.suggestions[i];
                if (sug) {
                    this.options.onSelect(sug.completion);
                    this.hide();
                }
            });
            el.addEventListener('mouseenter', () => {
                this.selectedIndex = i;
                this.render();
                this.updateGhostTextFromSelection();
            });
        });
        const selectedEl = this.dropdown.querySelector('.search-autocomplete-option.selected');
        if (selectedEl) {
            selectedEl.scrollIntoView({ block: 'nearest' });
        }
    }
    position(anchorRect) {
        if (!this.dropdown)
            return;
        const dropdownHeight = this.dropdown.offsetHeight || 200;
        const viewportHeight = window.innerHeight;
        let top = anchorRect.bottom + 4;
        let left = anchorRect.left;
        if (top + dropdownHeight > viewportHeight - 10) {
            top = anchorRect.top - dropdownHeight - 4;
        }
        const maxLeft = window.innerWidth - (this.dropdown.offsetWidth || 250) - 10;
        left = Math.min(left, maxLeft);
        this.dropdown.style.position = 'fixed';
        this.dropdown.style.top = `${top}px`;
        this.dropdown.style.left = `${left}px`;
        this.dropdown.style.zIndex = '10000';
    }
    destroy() {
        if (this.dropdown) {
            this.dropdown.remove();
            this.dropdown = null;
        }
        if (this.ghostOverlay) {
            this.ghostOverlay.remove();
            this.ghostOverlay = null;
        }
    }
}
//# sourceMappingURL=Autocomplete.js.map